import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import moment from "moment";
import Filter from "../Property/components/Filter";
import TableFooter from '../Common/TableFooter';
import CommonExportButtons from '../Common/CommonExportButtons';
import PageSizeSelector from '../Common/PageSizeSelector';
import SkeletonTable from '../Common/SkeletonTable';
import { Link } from 'react-router-dom';

import {
  getPropertiesAction,
  getPropertyFiltersAction,
} from "../../../store/actions/property/PropertyActions";

const PropertyReport = () => {
  const dispatch = useDispatch();
  const { properties, loading } = useSelector((state) => state.property);
  const { filters } = useSelector((state) => state.property);
  const totalPages = properties?.pages || 1;
  const propertyRows = properties?.rows || [];
  
  const [openMenu, setOpenMenu] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterQuery, setFilterQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [orderBy, setOrderBy] = useState('');
  const [orderByDir, setOrderByDir] = useState('desc');

  // Filter states
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
  });
  const [priceRange, setPriceRange] = useState([0, 0]);
  const [areaRange, setAreaRange] = useState([0, 0]);
  const [customer, setCustomer] = useState("");
  const [propertyTypeId, setPropertyTypeId] = useState("");
  const [listingTypeId, setListingTypeId] = useState("");
  const [availabilityTypeId, setAvailabilityTypeId] = useState("");
  const [adStatusId, setAdStatusId] = useState("");
  const [isVerified, setIsVerified] = useState(false);
  const [isFeatured, setIsFeatured] = useState(false);
  const [location, setLocation] = useState({
    lattitude: "",
    longitude: "",
  });

  // Options
  const [propertyTypeOptions, setPropertyTypeOptions] = useState([]);
  const [listingTypeOptions, setListingTypeOptions] = useState([]);
  const [availabilityTypeOptions, setAvailabilityTypeOptions] = useState([]);
  const adStatusOptions = [
    {value: 'pending', label: 'Pending'},
    {value: 'active', label: 'Active'},
    {value: 'sold', label: 'Sold'},
    {value: 'blocked', label: 'Blocked'},
    {value: 'rejected', label: 'Rejected'}
  ];

  // Define skeleton table columns
  const skeletonColumns = [
    { title: 'Title', width: 150 },
    { title: 'Price', width: 100 },
    { title: 'Property Type', width: 120 },
    { title: 'Listing Type', width: 100 },
    { title: 'Location', width: 150 },
    { title: 'Customer Name', width: 120 },
    { title: 'Phone', width: 100 },
    { title: 'Created At', width: 120 },
    { title: 'Status', width: 80 },
    { title: 'Action', width: 80, height: 32 }
  ];

  const buildSearchQuery = () => {
    const near =
      location && location.latitude && location.longitude && areaRange
        ? `${location.latitude},${location.longitude},${areaRange[1]}`
        : "";
    const property = propertyTypeId ? propertyTypeId : "";
    const listing = listingTypeId ? listingTypeId : "";
    const availability = availabilityTypeId ? availabilityTypeId : "";
    const adStatus = adStatusId ? adStatusId : "";
    const customerId = customer ? customer : "";
    const price =
      priceRange && priceRange[1] > 0
        ? `btw-${priceRange[0]},${priceRange[1]}`
        : "";
    const search = searchTerm ? searchTerm : "";

    const createdAt =
      dateRange?.startDate && dateRange?.endDate
        ? `btw-${dateRange.startDate.format(
            "YYYY/MM/DD"
          )},${dateRange.endDate.format("YYYY/MM/DD")}`
        : "";

    const params = new URLSearchParams();

    if (near) params.append("filter_near", near);
    if (property) params.append("filter_propertyTypeId", property);
    if (listing) params.append("filter_listingTypeId", listing);
    if (availability) params.append("filter_availabilityStatusId", availability);
    if (adStatus) params.append("filter_adStatus", adStatus);
    if (price) params.append("filter_price", price);
    if (search) params.append("search", search);
    if(customerId) params.append("filter_customerId", customerId);
    if (createdAt) params.append("filter_createdAt", createdAt); 
    if (isVerified) params.append("filter_isVerified", true);
    if (isFeatured) params.append("filter_isFeatured", true);
    if (orderBy) {
      params.append('orderBy', orderBy);
      params.append('orderByDir', orderByDir);
    }

    const queryString = `${params.toString()}`;
    return queryString;
  };

  const handleSort = (field) => {
    if (orderBy === field) {
      setOrderByDir(orderByDir === 'desc' ? 'asc' : 'desc');
    } else {
      setOrderBy(field);
      setOrderByDir('desc');
    }
    setCurrentPage(1);
  };

  const onSubmit = async () => {
    const query = buildSearchQuery();
    setFilterQuery(query);
    setCurrentPage(1);
  };

  const onPlaceSelected = async (place) => {
    const updated = {
      latitude: "",
      longitude: "",
    };
    if (place.geometry?.location) {
      updated.latitude = place.geometry.location.lat();
      updated.longitude = place.geometry.location.lng();
    }
    setLocation(updated);
  };

  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return 'bg-success';
      case 'pending': return 'bg-warning';
      case 'sold': return 'bg-info';
      case 'blocked': return 'bg-danger';
      case 'rejected': return 'bg-secondary';
      default: return 'bg-secondary';
    }
  };

  useEffect(() => {
    if (filters && Object.keys(filters).length > 0) {
      setPropertyTypeOptions(
        filters.propertyTypes.map((type) => ({
          value: type._id,
          label: type.name,
        }))
      );
      setListingTypeOptions(
        filters.listingTypes.map((type) => ({
          value: type._id,
          label: type.name,
        }))
      );
      setAvailabilityTypeOptions(
        filters.availabilityStatuses.map((type) => ({
          value: type._id,
          label: type.name,
        }))
      );
    }
  }, [filters]);

  useEffect(() => {
    let query = buildSearchQuery();
    let effectivePageSize = pageSize;
    if (pageSize === 100000) {
      // 'All' option selected
      query = query ? `${query}&noPagination=true` : 'noPagination=true';
      effectivePageSize = 100000;
    }
    dispatch(getPropertiesAction(currentPage, effectivePageSize, "", `populate=customerId,propertyTypeId,listingTypeId,availabilityStatusId&${query}`));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, currentPage, pageSize, filterQuery, orderBy, orderByDir]);

  useEffect(() => {
    dispatch(getPropertyFiltersAction());
  }, [dispatch]);
  return (
    <>
      <div className='form-head page-titles d-flex align-items-center'>
        <div className='me-auto d-lg-block'>
          <h4 className='mb-1'>Property Report</h4>
        </div>
      </div>
      <Filter
        setOpenMenu={setOpenMenu}
        openMenu={openMenu}
        propertyTypeOptions={propertyTypeOptions}
        listingTypeOptions={listingTypeOptions}
        priceRange={priceRange}
        areaRange={areaRange}
        setPriceRange={setPriceRange}
        setAreaRange={setAreaRange}
        onPlaceSelected={onPlaceSelected}
        onSubmit={onSubmit}
        setSearchTerm={setSearchTerm}
        setPropertyTypeId={setPropertyTypeId}
        setListingTypeId={setListingTypeId}
        setLocation={setLocation}
        startDate={dateRange.startDate}
        endDate={dateRange.endDate}
        setDateRange={setDateRange}
        report={true}
        setCustomer={setCustomer}
        availabilityTypeOptions={availabilityTypeOptions}
        setAvailabilityTypeId={setAvailabilityTypeId}
        adStatusOptions={adStatusOptions}
        setAdStatusId={setAdStatusId}
        isFeatured={isFeatured}
        setIsFeatured={setIsFeatured}
        isVerified={isVerified}
        setIsVerified={setIsVerified}
      />
      
      <div className='row'>
        <div className="d-flex mb-2 align-items-center justify-content-between">
          <PageSizeSelector
            pageSize={pageSize}
            setPageSize={setPageSize}
            setCurrentPage={setCurrentPage}
          />
          <CommonExportButtons
            data={propertyRows}
            exportHeaders={[
              { label: 'Title', key: 'title' },
              { label: 'Price', key: 'price' },
              { label: 'Property Type', key: 'propertyTypeId.name' },
              { label: 'Listing Type', key: 'listingTypeId.name' },
              { label: 'Location', key: 'address' },
              { label: 'Customer Name', key: 'customerId.name' },
              { label: 'Phone', key: 'customerId.phone' },
              { label: 'Created At', key: 'createdAt' },
              { label: 'Status', key: 'adStatus' },
            ]}
            filename="property-report"
            pdfTitle="Property Report"
            totalText="Total Properties"
            pdfOrientation="landscape"
            pdfHeaderColor={[41, 128, 185]}
            dateFields={['createdAt']}
            priceFields={['price']}
          />
        </div>
        <div className='col-xl-12'>
          <div className='table-responsive table-hover fs-14 order-list-table'>
            {loading ? (
              <SkeletonTable columns={skeletonColumns} rows={8} />
            ) : (!loading && propertyRows.length === 0) ? (
              <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 220, width: '100%' }}>
                <div className="card shadow-sm border-0 p-4 text-center w-100" style={{ background: '#f8fafc' }}>
                  <div style={{ fontSize: 48, color: '#adb5bd', marginBottom: 12 }}>
                    <i className="fa fa-search" />
                  </div>
                  <div style={{ fontWeight: 600, fontSize: 20, color: '#495057', marginBottom: 4 }}>
                    No Data Found
                  </div>
                  <div style={{ color: '#868e96', fontSize: 15 }}>
                    Try adjusting your filters or search criteria.
                  </div>
                </div>
              </div>
            ) : (
              <table className='table display table-hover display mb-4 dataTablesCard card-table dataTable no-footer' id='propertyTable'>
                <thead>
                  <tr role='row'>
                    <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('title')}>
                      Title
                      <span style={{ marginLeft: 4, fontSize: 13, display: 'inline-block', verticalAlign: 'middle' }}>
                        <i className="fa fa-caret-up" style={{ color: orderBy === 'title' && orderByDir === 'asc' ? '#007bff' : '#ccc', opacity: orderBy === 'title' || true ? 1 : 0.5, display: 'block', lineHeight: 1 }} />
                        <i className="fa fa-caret-down" style={{ color: orderBy === 'title' && orderByDir === 'desc' ? '#007bff' : '#ccc', opacity: orderBy === 'title' || true ? 1 : 0.5, display: 'block', lineHeight: 1, marginTop: -2 }} />
                      </span>
                    </th>
                    <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('price')}>
                      Price
                      <span style={{ marginLeft: 4, fontSize: 13, display: 'inline-block', verticalAlign: 'middle' }}>
                        <i className="fa fa-caret-up" style={{ color: orderBy === 'price' && orderByDir === 'asc' ? '#007bff' : '#ccc', opacity: orderBy === 'price' || true ? 1 : 0.5, display: 'block', lineHeight: 1 }} />
                        <i className="fa fa-caret-down" style={{ color: orderBy === 'price' && orderByDir === 'desc' ? '#007bff' : '#ccc', opacity: orderBy === 'price' || true ? 1 : 0.5, display: 'block', lineHeight: 1, marginTop: -2 }} />
                      </span>
                    </th>
                    <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('propertyTypeId.name')}>
                      Property Type
                      <span style={{ marginLeft: 4, fontSize: 13, display: 'inline-block', verticalAlign: 'middle' }}>
                        <i className="fa fa-caret-up" style={{ color: orderBy === 'propertyTypeId.name' && orderByDir === 'asc' ? '#007bff' : '#ccc', opacity: orderBy === 'propertyTypeId.name' || true ? 1 : 0.5, display: 'block', lineHeight: 1 }} />
                        <i className="fa fa-caret-down" style={{ color: orderBy === 'propertyTypeId.name' && orderByDir === 'desc' ? '#007bff' : '#ccc', opacity: orderBy === 'propertyTypeId.name' || true ? 1 : 0.5, display: 'block', lineHeight: 1, marginTop: -2 }} />
                      </span>
                    </th>
                    <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('listingTypeId.name')}>
                      Listing Type
                      <span style={{ marginLeft: 4, fontSize: 13, display: 'inline-block', verticalAlign: 'middle' }}>
                        <i className="fa fa-caret-up" style={{ color: orderBy === 'listingTypeId.name' && orderByDir === 'asc' ? '#007bff' : '#ccc', opacity: orderBy === 'listingTypeId.name' || true ? 1 : 0.5, display: 'block', lineHeight: 1 }} />
                        <i className="fa fa-caret-down" style={{ color: orderBy === 'listingTypeId.name' && orderByDir === 'desc' ? '#007bff' : '#ccc', opacity: orderBy === 'listingTypeId.name' || true ? 1 : 0.5, display: 'block', lineHeight: 1, marginTop: -2 }} />
                      </span>
                    </th>
                    <th>Location</th>
                    <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('customerId.name')}>
                      Customer Name
                      <span style={{ marginLeft: 4, fontSize: 13, display: 'inline-block', verticalAlign: 'middle' }}>
                        <i className="fa fa-caret-up" style={{ color: orderBy === 'customerId.name' && orderByDir === 'asc' ? '#007bff' : '#ccc', opacity: orderBy === 'customerId.name' || true ? 1 : 0.5, display: 'block', lineHeight: 1 }} />
                        <i className="fa fa-caret-down" style={{ color: orderBy === 'customerId.name' && orderByDir === 'desc' ? '#007bff' : '#ccc', opacity: orderBy === 'customerId.name' || true ? 1 : 0.5, display: 'block', lineHeight: 1, marginTop: -2 }} />
                      </span>
                    </th>
                    <th>Phone</th>
                    <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('createdAt')}>
                      Created At
                      <span style={{ marginLeft: 4, fontSize: 13, display: 'inline-block', verticalAlign: 'middle' }}>
                        <i className="fa fa-caret-up" style={{ color: orderBy === 'createdAt' && orderByDir === 'asc' ? '#007bff' : '#ccc', opacity: orderBy === 'createdAt' || true ? 1 : 0.5, display: 'block', lineHeight: 1 }} />
                        <i className="fa fa-caret-down" style={{ color: orderBy === 'createdAt' && orderByDir === 'desc' ? '#007bff' : '#ccc', opacity: orderBy === 'createdAt' || true ? 1 : 0.5, display: 'block', lineHeight: 1, marginTop: -2 }} />
                      </span>
                    </th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {propertyRows.map((item, idx) => (
                    <tr key={item._id}>
                      <td>
                        <div style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.title}>
                          {item.title}
                        </div>
                      </td>
                      <td>₹{Number(item.price).toLocaleString()}</td>
                      <td>{item.propertyTypeId?.name || 'N/A'}</td>
                      <td>{item.listingTypeId?.name || 'N/A'}</td>
                      <td>
                        <div style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.address}>
                          {item.address || 'N/A'}
                        </div>
                      </td>
                      <td>{item.customerId?.name || 'N/A'}</td>
                      <td>{item.customerId?.phone || 'N/A'}</td>
                      <td>{moment(item.createdAt).format("DD/MM/YYYY, hh:mm A")}</td>
                      <td>
                        <span className={`badge ${getStatusBadgeClass(item.adStatus)}`}>
                          {item.adStatus?.charAt(0).toUpperCase() + item.adStatus?.slice(1) || 'N/A'}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex">
                          <Link to={`/property-details/${item._id}`} className="btn btn-secondary btn-sm shadow">
                            <i className="fa fa-eye me-1"></i>
                            View
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
      {(!loading && propertyRows.length > 0) && (
        <TableFooter
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          totalEntries={properties?.total || propertyRows.length}
          onPageChange={setCurrentPage}
        />
      )}
    </>
  );
};

export default PropertyReport;
